import { Injectable } from '@angular/core';
import { DynamicField, DynamicTable } from '@models/dynamic-form.interface';

/**
 * Client-side expression evaluator that mirrors FluidSys_New CalculationEngineService.
 *
 * Supported syntax:
 *   - Arithmetic:        density * 0.1198,  v600 - v300,  (a + b) / 2
 *   - Field references:  any field name as defined in FormField.name
 *   - Math functions:    round(x, 2), floor(x), ceiling(x), abs(x), sqrt(x),
 *                        pow(x, y), min(a,b,...), max(a,b,...), sum(a,b,...), avg(a,b,...),
 *                        sin(x), cos(x), tan(x), log(x), log10(x)
 *   - String functions:  concat(a,b,...), upper(s), lower(s), trim(s),
 *                        substring(s,start,len), length(s), replace(s,from,to)
 *   - Conditional:       IF(condition, trueValue, falseValue)
 *   - Case:              CASE WHEN cond1 THEN val1 WHEN cond2 THEN val2 ELSE default END
 *   - Safe division:     denominator 0 returns 0 (no exception)
 */
@Injectable({ providedIn: 'root' })
export class CalculationEngineService {
  private readonly MATH_KEYWORDS = new Set([
    'round', 'floor', 'ceiling', 'abs', 'sqrt', 'pow',
    'min', 'max', 'sum', 'avg',
    'sin', 'cos', 'tan', 'log', 'log10',
  ]);

  private readonly STRING_KEYWORDS = new Set([
    'concat', 'upper', 'lower', 'trim', 'substring', 'length', 'replace',
  ]);

  private readonly RESERVED = new Set([
    'if', 'else', 'then', 'when', 'case', 'end',
    'and', 'or', 'not', 'true', 'false', 'null', 'pi', 'e',
  ]);

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Evaluates an expression string.
   *
   * @param expression  Raw expression, e.g. "density * 0.1198" or "IF(v600 > 0, v600 - v300, 0)"
   * @param formValues  Map of fieldName → current value
   * @param fields      Full field list (used to identify valid field names)
   */
  public evaluate(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): unknown {
    if (!expression?.trim()) return null;

    try {
      const trimmed = expression.trim();

      if (/^if\s*\(/i.test(trimmed)) {
        return this.evaluateIf(trimmed, formValues, fields);
      }

      if (/^case\b/i.test(trimmed)) {
        return this.evaluateCase(trimmed, formValues, fields);
      }

      const parsed = this.parseExpression(trimmed, formValues, fields);
      return this.evaluateArithmetic(parsed);
    } catch {
      return null;
    }
  }

  /** Returns true if the expression contains operators, functions or field refs */
  public isCalculationExpression(expression: string): boolean {
    if (!expression) return false;
    const operators = ['+', '-', '*', '/', '(', '='];
    const fnPattern = /\b(round|floor|ceiling|abs|sqrt|pow|min|max|sum|avg|sin|cos|tan|log|log10|concat|upper|lower|trim|substring|length|replace|if|case)\b/i;
    return operators.some((op) => expression.includes(op)) || fnPattern.test(expression);
  }

  public safeDivide(numerator: number, denominator: number): number {
    return denominator === 0 ? 0 : numerator / denominator;
  }

  public formatNumber(value: number, decimalPlaces: number): string {
    return value.toFixed(decimalPlaces);
  }

  /**
   * Evaluates a calculation-cell expression that may reference cells across tables,
   * e.g. "table-a[1,1] + table-b[2,0]" or "[1,1] * 2" (bracket-only refs resolve against
   * `currentTableId`). Mirrors FluidSys_New TableLayoutService.EvaluateCellCalculation.
   *
   * @param expression      Raw cell expression, possibly containing TableId[row,col] refs
   * @param tables          All tables in the form layout (needed to resolve cross-table refs)
   * @param currentTableId  ID of the table that owns the cell being evaluated
   * @param formValues      Map of fieldName → current value
   * @param fields          Full field list
   */
  public evaluateCellExpression(
    expression: string,
    tables: DynamicTable[],
    currentTableId: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): unknown {
    if (!expression?.trim()) return null;

    try {
      const resolved = this.resolveCellReferences(expression, tables, currentTableId, formValues, fields);
      return this.evaluate(resolved, formValues, fields);
    } catch {
      return null;
    }
  }

  /** Replaces every `TableId[row,col]` / `[row,col]` reference in the expression with its resolved value */
  private resolveCellReferences(
    expression: string,
    tables: DynamicTable[],
    currentTableId: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): string {
    const pattern = /(\w+)?\[(\d+)\s*,\s*(\d+)\]/g;
    let result = expression;
    let match: RegExpExecArray | null;
    const seen = new Set<string>();

    while ((match = pattern.exec(expression)) !== null) {
      const literal = match[0];
      if (seen.has(literal)) continue;
      seen.add(literal);

      const tableId = match[1] || currentTableId;
      const row = Number(match[2]);
      const col = Number(match[3]);
      const table = tables.find((t) => t.id === tableId) ?? tables.find((t) => t.id === currentTableId);
      const cellValue = this.getCellValue(table, row, col, tables, formValues, fields);
      const literalValue = cellValue !== null && cellValue !== undefined ? String(cellValue) : '0';

      result = result.split(literal).join(literalValue);
    }

    return result;
  }

  /** Resolves the runtime value of a single table cell (field value, nested calculation, or static text) */
  private getCellValue(
    table: DynamicTable | undefined,
    row: number,
    col: number,
    allTables: DynamicTable[],
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): unknown {
    if (!table) return null;
    const cell = table.cells.find((c) => c.row === row && c.col === col);
    if (!cell) return null;

    switch (cell.cellType) {
      case 'field': {
        if (!cell.fieldId) return null;
        const field = fields.find((f) => f.id === cell.fieldId);
        if (!field) return null;
        return formValues[field.name] ?? formValues[field.id] ?? null;
      }
      case 'calculation': {
        if (!cell.expression) return null;
        const resolved = this.resolveCellReferences(cell.expression, allTables, table.id, formValues, fields);
        return this.evaluate(resolved, formValues, fields);
      }
      case 'staticText':
        return cell.text ?? '';
      default:
        return null;
    }
  }

  /** Evaluates a condition string like "> 5", "== 'offshore'", "== true" against a value */
  public evaluateCondition(fieldValue: unknown, condition: string): boolean {
    if (!condition?.trim()) return true;

    const c = condition.trim();
    const ops: Array<[string, (a: number, b: number) => boolean]> = [
      ['>=', (a, b) => a >= b],
      ['<=', (a, b) => a <= b],
      ['!=', (a, b) => a !== b],
      ['==', (a, b) => a === b],
      ['>', (a, b) => a > b],
      ['<', (a, b) => a < b],
    ];

    for (const [op, fn] of ops) {
      if (c.startsWith(op)) {
        const rhs = c.slice(op.length).trim().replace(/^['"]|['"]$/g, '');
        const lhsNum = Number(fieldValue);
        const rhsNum = Number(rhs);
        if (!Number.isNaN(lhsNum) && !Number.isNaN(rhsNum)) {
          return fn(lhsNum, rhsNum);
        }
        // String / boolean comparison
        const lhsStr = String(fieldValue ?? '');
        if (op === '==') return lhsStr === rhs;
        if (op === '!=') return lhsStr !== rhs;
        return false;
      }
    }

    // Bare boolean check
    if (c === 'true') return Boolean(fieldValue);
    if (c === 'false') return !fieldValue;

    return false;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /** IF(condition, trueExpr, falseExpr) */
  private evaluateIf(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): unknown {
    // Extract three comma-separated arguments inside the outer IF(...)
    const inner = this.extractFunctionArgs(expression.replace(/^if\s*/i, ''));
    if (!inner || inner.length < 3) return null;

    const [condExpr, trueExpr, falseExpr] = inner;
    const condResult = this.evaluateBooleanExpr(condExpr.trim(), formValues, fields);
    return this.evaluate(condResult ? trueExpr.trim() : falseExpr.trim(), formValues, fields);
  }

  /** CASE WHEN cond THEN val ... ELSE default END */
  private evaluateCase(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): unknown {
    const whenPattern = /when\s+(.+?)\s+then\s+(.+?)(?=\s+when|\s+else|\s+end|$)/gi;
    const elsePattern = /else\s+(.+?)\s+end/i;

    let match: RegExpExecArray | null;
    while ((match = whenPattern.exec(expression)) !== null) {
      const cond = match[1].trim();
      const val = match[2].trim();
      if (this.evaluateBooleanExpr(cond, formValues, fields)) {
        return this.evaluate(val, formValues, fields);
      }
    }

    const elseMatch = elsePattern.exec(expression);
    if (elseMatch) {
      return this.evaluate(elseMatch[1].trim(), formValues, fields);
    }

    return null;
  }

  /**
   * Evaluates a boolean comparison expression like "density > 5" or "location == 'offshore'".
   * When the expression does not contain a comparison operator it falls back to falsy/truthy.
   */
  private evaluateBooleanExpr(
    expr: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): boolean {
    const parsed = this.parseExpression(expr, formValues, fields);
    const ops: Array<[string, (a: string, b: string) => boolean]> = [
      ['>=', (a, b) => Number(a) >= Number(b)],
      ['<=', (a, b) => Number(a) <= Number(b)],
      ['!=', (a, b) => a.trim() !== b.trim()],
      ['==', (a, b) => a.trim() === b.trim()],
      ['>', (a, b) => Number(a) > Number(b)],
      ['<', (a, b) => Number(a) < Number(b)],
    ];

    for (const [op, fn] of ops) {
      const idx = parsed.indexOf(op);
      if (idx !== -1) {
        const lhs = this.tryNumericEval(parsed.slice(0, idx).trim());
        const rhs = this.tryNumericEval(parsed.slice(idx + op.length).trim().replace(/^['"]|['"]$/g, ''));
        return fn(String(lhs), String(rhs));
      }
    }

    const result = this.evaluateArithmetic(parsed);
    return Boolean(result);
  }

  /**
   * Substitutes field references and resolves function calls, returning a numeric expression
   * string that can be passed to evaluateArithmetic.
   */
  private parseExpression(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): string {
    let result = expression;

    // 1. Resolve math functions (innermost first via repeated passes)
    result = this.replaceMathFunctions(result, formValues, fields);

    // 2. Resolve string functions
    result = this.replaceStringFunctions(result, formValues, fields);

    // 3. Replace field references with their current values
    const fieldRefs = this.findFieldReferences(result, fields);
    for (const ref of fieldRefs) {
      const fieldName = ref.toLowerCase();
      const matchedField = fields.find((f) => f.name.toLowerCase() === fieldName);
      if (!matchedField) continue;

      const raw = formValues[matchedField.name] ?? formValues[matchedField.id] ?? '0';
      const formatted = this.formatForExpression(raw, matchedField);
      // Replace all occurrences of the field name as a whole word
      result = result.replace(new RegExp(`\\b${ref}\\b`, 'g'), formatted);
    }

    return result;
  }

  /** Replaces math function calls with their evaluated result */
  private replaceMathFunctions(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): string {
    const fns: Record<string, (args: number[]) => number> = {
      round: (a) => Number(a[0].toFixed(a[1] ?? 0)),
      floor: (a) => Math.floor(a[0]),
      ceiling: (a) => Math.ceil(a[0]),
      abs: (a) => Math.abs(a[0]),
      sqrt: (a) => Math.sqrt(a[0]),
      pow: (a) => Math.pow(a[0], a[1]),
      min: (a) => Math.min(...a),
      max: (a) => Math.max(...a),
      sum: (a) => a.reduce((acc, v) => acc + v, 0),
      avg: (a) => a.reduce((acc, v) => acc + v, 0) / (a.length || 1),
      sin: (a) => Math.sin(a[0]),
      cos: (a) => Math.cos(a[0]),
      tan: (a) => Math.tan(a[0]),
      log: (a) => Math.log(a[0]),
      log10: (a) => Math.log10(a[0]),
    };

    let result = expression;

    // Replace from innermost outward via iteration (up to 5 nested levels)
    for (let pass = 0; pass < 5; pass++) {
      let changed = false;
      for (const [name, fn] of Object.entries(fns)) {
        const pattern = new RegExp(`\\b${name}\\s*\\(([^()]+)\\)`, 'gi');
        result = result.replace(pattern, (_match, argsStr: string) => {
          changed = true;
          const args = argsStr
            .split(',')
            .map((a) => {
              const parsed = this.parseExpression(a.trim(), formValues, fields);
              return Number(this.tryNumericEval(parsed));
            })
            .filter((v) => Number.isFinite(v));

          if (args.length === 0) return '0';
          const value = fn(args);
          return Number.isFinite(value) ? String(value) : '0';
        });
      }
      if (!changed) break;
    }

    return result;
  }

  /** Replaces string function calls with their evaluated result (quoted) */
  private replaceStringFunctions(
    expression: string,
    formValues: Record<string, unknown>,
    fields: DynamicField[]
  ): string {
    const fns: Record<string, (args: string[]) => string> = {
      concat: (a) => a.join(''),
      upper: (a) => a[0].toUpperCase(),
      lower: (a) => a[0].toLowerCase(),
      trim: (a) => a[0].trim(),
      substring: (a) => a[0].substring(Number(a[1]), Number(a[1]) + Number(a[2])),
      length: (a) => String(a[0].length),
      replace: (a) => a[0].replaceAll(a[1], a[2]),
    };

    let result = expression;

    for (const [name, fn] of Object.entries(fns)) {
      const pattern = new RegExp(`\\b${name}\\s*\\(([^()]+)\\)`, 'gi');
      result = result.replace(pattern, (_match, argsStr: string) => {
        const args = argsStr
          .split(',')
          .map((a) => {
            const parsed = this.parseExpression(a.trim(), formValues, fields);
            return parsed.replace(/^['"]|['"]$/g, '');
          });
        if (args.length === 0) return '""';
        try {
          return `"${fn(args)}"`;
        } catch {
          return '""';
        }
      });
    }

    return result;
  }

  /**
   * Evaluates a pure numeric expression string like "8 + 4 * 2.5".
   * Returns a number or string result.
   */
  private evaluateArithmetic(expression: string): unknown {
    const trimmed = expression.trim();

    // Safe division pass
    const safeDivExpr = trimmed.replace(
      /([\d.]+)\s*\/\s*([\d.]+)/g,
      (_, num: string, den: string) => String(this.safeDivide(Number(num), Number(den)))
    );

    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(`"use strict"; return (${safeDivExpr});`);
      const result: unknown = fn();
      if (typeof result === 'number' && Number.isFinite(result)) return result;
      if (typeof result === 'boolean') return result;
      if (typeof result === 'string') return result;
      return null;
    } catch {
      // Try stripping outer quotes (string literal result)
      const unquoted = trimmed.replace(/^['"]|['"]$/g, '');
      if (unquoted !== trimmed) return unquoted;
      return null;
    }
  }

  /** Finds all field name references inside an expression string */
  private findFieldReferences(expression: string, fields: DynamicField[]): string[] {
    const wordPattern = /\b([a-zA-Z][a-zA-Z0-9_]*)\b/g;
    const found = new Set<string>();
    let m: RegExpExecArray | null;

    while ((m = wordPattern.exec(expression)) !== null) {
      const word = m[1];
      if (this.RESERVED.has(word.toLowerCase())) continue;
      if (this.MATH_KEYWORDS.has(word.toLowerCase())) continue;
      if (this.STRING_KEYWORDS.has(word.toLowerCase())) continue;

      const matched = fields.find((f) => f.name.toLowerCase() === word.toLowerCase());
      if (matched) found.add(word);
    }

    return Array.from(found);
  }

  /** Formats a field value for numeric expression substitution */
  private formatForExpression(value: unknown, field: DynamicField): string {
    if (value === null || value === undefined || value === '') return '0';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    const num = Number(value);
    if (Number.isFinite(num)) return String(num);
    return `"${String(value)}"`;
  }

  /** Attempts to evaluate a simple expression, returns the string unchanged if it fails */
  private tryNumericEval(expression: string): unknown {
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(`"use strict"; return (${expression});`);
      const r: unknown = fn();
      return r;
    } catch {
      return expression.replace(/^['"]|['"]$/g, '');
    }
  }

  /**
   * Extracts top-level comma-separated arguments from a parenthesised function call string.
   * e.g. "(density > 0, density * 2, 0)" → ["density > 0", "density * 2", "0"]
   */
  private extractFunctionArgs(expression: string): string[] | null {
    const trimmed = expression.trim();
    if (!trimmed.startsWith('(') || !trimmed.endsWith(')')) return null;

    const inner = trimmed.slice(1, -1);
    const args: string[] = [];
    let depth = 0;
    let current = '';

    for (const ch of inner) {
      if (ch === '(') depth++;
      else if (ch === ')') depth--;

      if (ch === ',' && depth === 0) {
        args.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }

    if (current.trim()) args.push(current.trim());
    return args;
  }
}
